// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { maybeWaitForPremiumModal } from '../utils.tsx';
import type { Props, State } from '../../types.tsx';
import { UNIFIED_SHARE_DIALOG_SPOTLIGHT } from '../../constants.tsx';

export const loadUnifiedShareDialogSpotlightState =
	(): Action<State, Props, Promise<void>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().unifiedShareDialog.spotlightDismissed === 'boolean') {
			return;
		}

		try {
			const spotlightDismissed = await getUserProperty<boolean>(
				accountId,
				UNIFIED_SHARE_DIALOG_SPOTLIGHT,
			);

			const state = getState();

			setState({
				...state,
				unifiedShareDialog: {
					spotlightDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					unifiedShareDialog: {
						spotlightDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.unified-share-dialog-spotlight',
				`Failed to get user property with key: ${UNIFIED_SHARE_DIALOG_SPOTLIGHT}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	};

export const setUnifiedShareDialogSpotlightDismissed =
	(spotlightDismissed: boolean): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();
		const newUnifiedShareDialogState = {
			spotlightDismissed,
		};

		setState({
			...state,
			unifiedShareDialog: newUnifiedShareDialogState,
		});

		try {
			await setUserProperties(
				accountId,
				UNIFIED_SHARE_DIALOG_SPOTLIGHT,
				JSON.stringify(spotlightDismissed),
			);
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.unified-share-dialog-spotlight',
				`Failed to set user property with key: ${UNIFIED_SHARE_DIALOG_SPOTLIGHT}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	};
